import { useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Chip,
  Divider,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Skeleton,
} from '@mui/material';
import { useIsFetching } from '@tanstack/react-query';
import { analyticsKeys, useDeviceUptime } from '@/hooks/use-analytics';
import {
  deviceAnalyticsKeys,
  useDeviceEvents,
  useDeviceHeartbeatCadence,
} from '@/hooks/use-device-analytics';
import { DeviceUptime } from '@/components/device-uptime';
import { DeviceMetrics, type MetricMarker } from '@/components/device-metrics';
import { DeviceEvents } from '@/components/device-events';
import { classifyEvent } from '@/components/device-event-classify';
import {
  ANALYTICS_RANGES,
  CADENCE_MAX_LABEL,
  resolveWindow,
  type AnalyticsRangeKey,
} from '@/lib/analytics-range';
import { colors } from '@/theme/tokens';

interface DeviceAnalyticsProps {
  deviceId: string;
  /** devices.last_online (ISO) — passed through to the events panel. */
  lastOnline?: string;
}

/** Events are capped when fetched for the markers/verdict; a single device
 *  stays well under this over any of the offered windows. */
const MARKER_LIMIT = 500;

const SEVERITY_COLORS = {
  routine: colors.text.tertiary,
  notable: colors.warning.main,
  fault: colors.error.main,
} as const;

/**
 * The Analytics tab.
 *
 * Owns the one time range every panel reads from, and leads with the health
 * verdict — previously it was computed inside the events card and rendered
 * below two charts, which is the last place anyone looks. Because the window is
 * resolved once here and handed down, the panels' query keys agree and the
 * overlapping event/cadence requests are deduped by TanStack Query rather than
 * being fetched twice.
 */
export function DeviceAnalytics({ deviceId, lastOnline }: DeviceAnalyticsProps) {
  const [rangeKey, setRangeKey] = useState<AnalyticsRangeKey>('7d');

  // resolveWindow reads the clock, so it must be memoised on the range key —
  // computing it during render would mint a new query key every pass.
  const win = useMemo(() => resolveWindow(rangeKey), [rangeKey]);

  // Same params as the events panel's own aggregate query → one request.
  const { data: eventsResponse, isLoading: eventsLoading } = useDeviceEvents(deviceId, {
    start: win.start,
    end: win.end,
    limit: MARKER_LIMIT,
    offset: 0,
  });
  const allEvents = useMemo(() => eventsResponse?.data ?? [], [eventsResponse]);

  const { data: uptime, isLoading: uptimeLoading } = useDeviceUptime(deviceId, win.start, win.end);

  // Crash-loop is the one fault that never lands in the events table.
  const { data: cadence } = useDeviceHeartbeatCadence(
    deviceId,
    { start: win.start, end: win.end, interval: win.cadenceInterval },
    { enabled: win.cadenceEligible },
  );

  const markers: MetricMarker[] = useMemo(
    () =>
      allEvents.map((e) => {
        const cls = classifyEvent(e.event_type, e.event_data);
        return { ts: e.timestamp, label: cls.label, color: SEVERITY_COLORS[cls.severity] };
      }),
    [allEvents],
  );

  const faults = useMemo(
    () => allEvents.filter((e) => classifyEvent(e.event_type, e.event_data).severity === 'fault'),
    [allEvents],
  );
  const notables = useMemo(
    () => allEvents.filter((e) => classifyEvent(e.event_type, e.event_data).severity === 'notable'),
    [allEvents],
  );

  // Every analytics hook sets placeholderData: keepPreviousData, so on a range
  // change `isLoading` stays false and the panels keep rendering the PREVIOUS
  // window's data. Without this the switch looks instant and silently wrong.
  // Count the in-flight queries instead and mark the panels as stale until they
  // land — the skeletons only ever cover the very first load.
  const fetchingDevice = useIsFetching({ queryKey: deviceAnalyticsKeys.all });
  // Matched on the exact uptime key: analyticsKeys.all also covers the
  // fleet-wide Analytics page, which has nothing to do with this panel.
  const fetchingUptime = useIsFetching({
    queryKey: analyticsKeys.deviceUptime(deviceId, win.start, win.end),
  });
  const refreshing = fetchingDevice + fetchingUptime > 0;

  const verdictPending = eventsLoading && !eventsResponse;
  const degraded = !!cadence?.degraded;
  const status = degraded
    ? { label: 'Crash-looping', color: 'error' as const }
    : faults.length + notables.length > 0
      ? { label: 'Needs attention', color: 'warning' as const }
      : { label: 'Healthy', color: 'success' as const };

  // One line of plain English for why the verdict reads the way it does — a bare
  // chip makes the operator hunt through the table for the reason.
  const rationale = degraded
    ? 'Heartbeat cadence shows a crash-loop'
    : faults.length > 0
      ? `${faults.length} problem${faults.length > 1 ? 's' : ''} in the ${win.label.toLowerCase()}`
      : notables.length > 0
        ? `${notables.length} notable event${notables.length > 1 ? 's' : ''} in the ${win.label.toLowerCase()}`
        : `No problems in the ${win.label.toLowerCase()}`;

  const lastSeen = lastOnline ? relativeTime(lastOnline) : '—';

  return (
    <Card variant="outlined">
      <CardHeader
        title="Analytics"
        titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
        subheader="Health, metrics and events over one shared window"
        subheaderTypographyProps={{ variant: 'body2' }}
      />

      {/* ── Verdict, first ─────────────────────────────────────────────── */}
      <Box sx={{ px: 2, pb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        {/* An empty event list scores as "Healthy", so showing the chip before
            the data arrives would flash a green all-clear on a faulty device.
            Hold the skeleton until there is something to judge. */}
        {verdictPending ? (
          <>
            <Skeleton variant="rounded" width={86} height={24} />
            <Skeleton variant="text" width={210} />
          </>
        ) : (
          <>
            <Chip
              size="small"
              color={status.color}
              variant={status.color === 'success' ? 'outlined' : 'filled'}
              label={status.label}
            />
            <Typography variant="body2" color="text.secondary">
              {rationale}
            </Typography>
          </>
        )}

        <Box sx={{ display: 'flex', gap: 3.5, flexWrap: 'wrap', ml: 'auto' }}>
          <Fact
            label="Uptime"
            value={uptime ? `${uptime.uptime_percent.toFixed(1)}%` : undefined}
            loading={uptimeLoading}
          />
          <Fact label="Events" value={String(allEvents.length)} loading={eventsLoading} />
          <Fact
            label="Problems"
            value={String(faults.length)}
            loading={eventsLoading}
            color={faults.length > 0 ? colors.error.main : undefined}
          />
          <Fact label="Last online" value={lastSeen} />
        </Box>
      </Box>

      {/* ── One range control for every panel below ────────────────────── */}
      <Box
        sx={{
          px: 2, py: 1.5,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 2, flexWrap: 'wrap',
          borderTop: '1px solid', borderBottom: '1px solid', borderColor: 'divider',
          bgcolor: 'background.default',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography
            variant="caption"
            sx={{ textTransform: 'uppercase', fontWeight: 600, color: 'text.secondary' }}
          >
            Range
          </Typography>
          <ToggleButtonGroup
            value={rangeKey}
            exclusive
            size="small"
            onChange={(_, val) => val && setRangeKey(val as AnalyticsRangeKey)}
            aria-label="Analytics time range"
          >
            {ANALYTICS_RANGES.map((r) => (
              <ToggleButton key={r.key} value={r.key}>{r.label}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        {/* State the cadence limit rather than letting the panel vanish. */}
        <Typography variant="caption" color="text.secondary">
          {refreshing
            ? 'Loading…'
            : win.cadenceEligible
              ? 'All panels share this range'
              : `Heartbeat cadence is only available up to ${CADENCE_MAX_LABEL}`}
        </Typography>
      </Box>

      {/* Sits in the 3px the range bar's bottom border already occupies, so
          nothing below it moves when a fetch starts or finishes. */}
      <Box sx={{ height: 3, mt: '-3px' }}>
        {refreshing && <LinearProgress sx={{ height: 3 }} />}
      </Box>

      <CardContent sx={{ pt: 2 }}>
        {/* Dim the panels while they still show the old window, so stale data
            never reads as the answer to the range you just picked. */}
        <Box
          aria-busy={refreshing}
          sx={{
            display: 'flex', flexDirection: 'column', gap: 2,
            opacity: refreshing ? 0.45 : 1,
            transition: 'opacity .15s ease',
            pointerEvents: refreshing ? 'none' : undefined,
          }}
        >
          <Section title="Availability">
            <DeviceUptime deviceId={deviceId} window={win} />
          </Section>

          <Divider />

          {/* Markers put each event on the same x-axis as CPU and memory, so a
              spike and the reboot that followed it line up visually. */}
          <DeviceMetrics deviceId={deviceId} window={win} markers={markers} />

          <Divider />

          <Section title="Events in this range">
            <DeviceEvents
              deviceId={deviceId}
              lastOnline={lastOnline}
              degraded={degraded}
              window={win}
            />
          </Section>
        </Box>
      </CardContent>
    </Card>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function Fact({
  label,
  value,
  loading,
  color,
}: {
  label: string;
  value?: string;
  loading?: boolean;
  color?: string;
}) {
  return (
    <Box>
      <Typography
        variant="caption"
        sx={{ display: 'block', textTransform: 'uppercase', fontWeight: 600, color: 'text.secondary' }}
      >
        {label}
      </Typography>
      {loading && value === undefined ? (
        <Skeleton variant="text" width={54} height={28} />
      ) : (
        <Typography
          sx={{ fontSize: 22, fontWeight: 700, lineHeight: 1.25, fontVariantNumeric: 'tabular-nums', color }}
        >
          {value ?? '—'}
        </Typography>
      )}
    </Box>
  );
}

function relativeTime(ts: string): string {
  const s = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (s < 45) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default DeviceAnalytics;
